import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const cases = [
  {
    industry: "Med Spa · Texas",
    headline: "$184,200",
    sub: "recovered in 47 days",
    metric: "From 6,200 dormant leads · 38 booked treatments",
    quote:
      "I'd written off our old database completely. They booked nineteen consults in the first week. We paid them nothing until the chairs were full.",
    name: "Marcela R.",
    role: "Owner, Lumen Aesthetics",
    img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1000",
  },
  {
    industry: "Brokerage · Florida",
    headline: "$612,500",
    sub: "GCI in one quarter",
    metric: "8,900 stale buyers re-engaged · 4 listings closed",
    quote:
      "What surprised me wasn't the volume — it was how on-brand every message felt. They actually listened.",
    name: "Devon P.",
    role: "Principal Broker",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=1000",
  },
];

function CaseCard({ c, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`case-card-${index + 1}`}
      className="group relative bg-[#0a0a0a] border border-white/10 hover:border-[#ccff00]/40 transition-colors overflow-hidden"
    >
      <div className="grid grid-cols-1 sm:grid-cols-5">
        {/* Photo — clipped frame + spotlight + parallax */}
        <div className="sm:col-span-2 relative overflow-hidden min-h-[220px] sm:min-h-full">
          <motion.img
            src={c.img}
            alt={c.name}
            style={{ y: imgY }}
            loading="lazy"
            className="absolute inset-0 w-full h-[125%] object-cover grayscale contrast-125 brightness-75 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/20 to-transparent" />
          {/* spotlight */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_50%_30%,rgba(204,255,0,0.18),transparent_60%)]" />
          <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] text-white/70 bg-black/40 backdrop-blur-sm px-2 py-1">
            {c.industry}
          </div>
        </div>

        {/* Content */}
        <div className="sm:col-span-3 p-8 lg:p-10">
          <div className="font-display text-5xl lg:text-6xl text-[#ccff00] mb-1 leading-none">
            {c.headline}
          </div>
          <div className="font-display text-lg text-white/70 mb-6 uppercase">
            {c.sub}
          </div>
          <div className="text-xs uppercase tracking-widest text-white/40 border-t border-white/10 pt-5 mb-5">
            {c.metric}
          </div>
          <blockquote className="text-white/80 text-base lg:text-lg leading-relaxed font-medium">
            &ldquo;{c.quote}&rdquo;
          </blockquote>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-[#ccff00]/20 border border-[#ccff00]/40 flex items-center justify-center font-display text-sm text-[#ccff00]">
              {c.name.charAt(0)}
            </div>
            <div>
              <div className="text-sm font-semibold">{c.name}</div>
              <div className="text-xs text-white/50">{c.role}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function Proof() {
  return (
    <section
      id="proof"
      data-testid="proof-section"
      className="relative py-24 lg:py-32 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-16">
          <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-4">
            / Receipts, not promises
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase max-w-3xl">
            Real numbers. Real businesses.
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {cases.map((c, i) => (
            <CaseCard key={i} c={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
